// @ts-nocheck
import * as React from "react";
import { Ic, LineChart, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";

// Organization snapshot history modal. RegionHistoryModal과 동일 구조로,
// 기관에서 추적하는 2개 지표(case_count / avg_response_mins)만 표시.
// 백엔드: GET /api/v1/organizations/:id/history?days=30
//
// 지표 메타(label/unit/color/fmt/inverted)는 컴포넌트 상수 — 새 지표 추가 시
// 백엔드 MERTICS 상수와 함께 한 줄 추가.
//
// thresholds: 운영 알람 기준선. 값은 운영 가이드와 같이 유지보수해야 함.
//   - case_count: 매니저당 담당 케이스 과부하 기준 (조정 가능 — 추후 Setting화 후보)
//   - avg_response_mins: 응급망 1차 응답 SLA 기준
//   둘 다 "값이 클수록 위험" 방향이라 inverted 여부와 무관하게 임계치 정의 자체는
//   "current >= value 일 때 초과" 의미로 통일(LineChart 사양).

const METRICS = [
  { key: "case_count",        label: "담당 케이스",    unit: "건",  color: "var(--rose-500)",
    thresholds: [
      { value: 40, color: "var(--amber-500)" }, // 주의
      { value: 60, color: "var(--rose-500)"  }, // 경고 — 매니저 과부하 위험
    ] },
  { key: "avg_response_mins", label: "평균 응답시간",  unit: "분",  color: "var(--sky-500)",
    fmt: v => v?.toFixed(1), inverted: true,
    thresholds: [
      { value: 15, color: "var(--amber-500)" }, // 주의 — SLA 근접
      { value: 30, color: "var(--rose-500)"  }, // 경고 — SLA 초과
    ] },
];

// 현재값과 임계치를 비교해 zone/색/라벨을 반환. 카드 헤더의 zone 칩에 사용.
function zoneFor(metric, current) {
  if (current == null || !metric.thresholds?.length) return null;
  const breached = metric.thresholds
    .filter(t => current >= t.value)
    .sort((a, b) => b.value - a.value)[0];
  if (!breached) return { label: "정상", color: "var(--green-600)", bg: "var(--green-50)" };
  // 가장 엄격(=값 큰) 임계치가 마지막. 그 다음(= 값 작은 임계치)을 "주의"로 본다.
  const sorted = [...metric.thresholds].sort((a, b) => a.value - b.value);
  const isCrit = breached.value === sorted[sorted.length - 1].value;
  return isCrit
    ? { label: "경고", color: "var(--rose-500)",  bg: "var(--rose-50)"  }
    : { label: "주의", color: "var(--amber-500)", bg: "var(--amber-50)" };
}

const PERIODS = [
  { key: 7,  label: "7일"  },
  { key: 30, label: "30일" },
  { key: 90, label: "90일" },
];

function fmtDate(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function OrganizationHistoryModal({ organization, onClose }) {
  useEscapeKey(onClose);
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    setLoading(true); setErr(null);
    fetch(`/api/v1/organizations/${organization.id}/history?days=${days}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`오류 (${r.status})`)))
      .then(setData)
      .catch(e => setErr(apiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [organization.id, days]);

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:640, maxHeight:'85vh', padding:0, display:'flex', flexDirection:'column'}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">{organization.name} · 운영 지표 이력</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>{organization.category} · 일일 스냅샷 시계열</div>
          </div>
          <div style={{display:'flex', alignItems:'center', gap:8}}>
            <div role="tablist" aria-label="기간 선택"
              style={{display:'inline-flex', background:'var(--bg-sub)', borderRadius:8, padding:2, border:'1px solid var(--border-soft)'}}>
              {PERIODS.map(p => {
                const active = p.key === days;
                return (
                  <button key={p.key} type="button" role="tab" aria-selected={active}
                    onClick={()=>setDays(p.key)}
                    style={{padding:'4px 10px', fontSize:11, fontWeight:700, border:'none', borderRadius:6, cursor:'pointer',
                            background: active ? 'var(--surface)' : 'transparent',
                            color:      active ? 'var(--ink-1)'   : 'var(--ink-3)',
                            boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none'}}>
                    {p.label}
                  </button>
                );
              })}
            </div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
          </div>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, overflow:'auto', flex:1}}>
          {loading && <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>불러오는 중…</div>}
          {!loading && !err && data && (data.series?.length || 0) === 0 && (
            <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>
              스냅샷 이력이 아직 없습니다. 통계 새로고침이 한 번도 실행되지 않은 기관입니다.
            </div>
          )}
          {!loading && !err && data && (data.series?.length || 0) > 0 && (
            <>
              <div className="muted" style={{fontSize:11, marginBottom:10}}>
                {fmtDate(data.series[0].recorded_on)} – {fmtDate(data.series[data.series.length-1].recorded_on)} · {data.series.length}개 샘플
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:12}}>
                {METRICS.map(m => {
                  const series = data.series.map(s => Number(s[m.key]) || 0);
                  const trend = data.trends?.[m.key];
                  const fmt = m.fmt || (v => v);
                  const current = series.length ? series[series.length - 1] : null;
                  const delta = trend?.delta;
                  const deltaSign = delta != null ? (delta > 0 ? "+" : delta < 0 ? "" : "") : "";
                  // inverted: 응답시간은 감소가 good → 색상 반전
                  const deltaColor = delta == null ? "var(--ink-3)"
                    : (m.inverted ? (delta < 0 ? "var(--green-600)" : "var(--rose-500)")
                                  : (delta > 0 ? "var(--green-600)" : delta < 0 ? "var(--rose-500)" : "var(--ink-3)"));
                  const zone = zoneFor(m, current);
                  // 임계치 라벨 — 사용자가 점선의 의미를 한눈에 알 수 있도록 차트 하단에 명시.
                  const thLegend = m.thresholds?.length
                    ? m.thresholds.map(t => `${fmt(t.value)}${m.unit}`).join(" · ")
                    : null;
                  return (
                    <div key={m.key} style={{padding:12, background:'var(--bg-sub)', borderRadius:10}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
                        <div className="muted" style={{fontSize:11, fontWeight:600}}>{m.label}</div>
                        {trend ? (
                          <div className="mono" style={{fontSize:10, color: deltaColor, fontWeight:700}}>
                            {deltaSign}{fmt(delta)}{m.unit} <span className="muted">vs {days}일 전</span>
                          </div>
                        ) : (
                          <div className="muted" style={{fontSize:10}}>비교 데이터 없음</div>
                        )}
                      </div>
                      <div style={{display:'flex', alignItems:'baseline', gap:6, marginTop:2}}>
                        <div style={{fontSize:20, fontWeight:800, color: zone?.color || m.color}}>{fmt(current)}</div>
                        <div className="muted" style={{fontSize:11}}>{m.unit}</div>
                        {zone && (
                          <span style={{marginLeft:'auto', fontSize:10, fontWeight:700, padding:'2px 6px',
                                         borderRadius:6, background: zone.bg, color: zone.color}}
                                aria-label={`현재 상태: ${zone.label}`}>
                            {zone.label}
                          </span>
                        )}
                      </div>
                      <div style={{marginTop:8}}>
                        <LineChart data={series} height={40} color={m.color} fill="var(--bg-sub)"
                                   thresholds={m.thresholds}/>
                      </div>
                      {thLegend && (
                        <div className="muted" style={{fontSize:10, marginTop:4}} title="임계치(주의 · 경고)">
                          임계치: {thLegend}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
